import React, { BaseSyntheticEvent, useEffect } from 'react'
import { IonItem, IonLabel, IonList, IonSearchbar, IonToggle, IonToolbar } from '@ionic/react'
import { IOption, IOptions, IPNNVOption } from '../../../common/common.types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import './MultiPickList.component.css'

export interface IMultiPickListProps {
  options: IOptions
  pnnvOptions: IOptions | null
  selectedOptions: IOptions
  selectedPNNVOption: IPNNVOption | null
  disabledOptions?: IOptions
  selectedColor?: string
  onChange: (options: IOptions, selectOption: IOption) => void
  onPNNVChange: (option: IOption) => void
  showPNNVs: boolean
}

const MultiPickList: React.FC<IMultiPickListProps> = (props: IMultiPickListProps) => {
  const {
    options,
    pnnvOptions,
    selectedOptions,
    selectedPNNVOption = null,
    disabledOptions,
    selectedColor,
    onChange,
    onPNNVChange,
    showPNNVs,
  } = props
  const [selected, setSelected] = useState(selectedOptions)
  const [filteredOptions, setFilteredOptions] = useState(options)
  const [searchValue, setSearchValue] = useState('')
  const { t } = useTranslation()

  useEffect(() => {
    setFilteredOptions(options)
  }, [options])

  useEffect(() => {
    setSelected(selectedOptions)
  }, [selectedOptions])

  const isOptionDisabled = (option: IOption) => {
    return disabledOptions?.some((o) => o.id === option.id)
  }

  const isOptionSelected = (option: IOption) => {
    return selected.some((o) => o.id === option.id)
  }

  const onOptionChange = (option: IOption) => {
    const isSelected = isOptionSelected(option)
    if (!isSelected) {
      selected.push(option)
    } else {
      if (selected.filter((o) => o.id === option.id).length > 0) {
        selected.splice(
          selected.findIndex((o) => o.id === option.id),
          1
        )
      }
    }
    setSelected(selected)
    onChange(selected, option)
  }

  const selectPNNVOption = (e: BaseSyntheticEvent, option: IOption) => {
    e.preventDefault()
    if (!onPNNVChange) return
    onPNNVChange(option)
  }

  const performSearch = (e: CustomEvent) => {
    const searchText = e.detail.value
    if (options) {
      const filteredOptions = options.filter((option) => option.name?.toLowerCase().includes(searchText.toLowerCase()))
      setSearchValue(searchText)
      setFilteredOptions(filteredOptions)
    }
  }

  const getPnnvSection = () => {
    return (
      showPNNVs &&
      pnnvOptions &&
      pnnvOptions.length > 0 && (
        <div className='page-pnnv-selection-section'>
          <hr className='picklist-divider' />
          <IonList>
            {pnnvOptions?.map((option: IOption) => (
              <IonItem
                key={`Option_${option.id} `}
                data-testid={`Option_${option.id}`}
                onClick={(e) => selectPNNVOption(e, option)}
                className={`${selectedPNNVOption?.id === option.id ? 'selected-pnnv-option' : ''} pnnv-option`}>
                <IonLabel id={`optionLabel_${option.id}`}>{option.name}</IonLabel>
              </IonItem>
            ))}
          </IonList>
        </div>
      )
    )
  }

  return (
    <div className='multi-segment-select'>
      <IonToolbar color='light'>
        <IonSearchbar
          data-testid='MutliPickListSearchbar'
          debounce={100}
          value={searchValue}
          onIonChange={performSearch}
          autocomplete='off'
        />
      </IonToolbar>
      <IonList class='ion-padding'>
        {filteredOptions &&
          filteredOptions.map((option) => {
            return (
              <IonItem key={`Option_${option.id}`} className='ion-no-padding' data-skip-auto-enhance>
                <IonLabel>{option.name}</IonLabel>
                <IonToggle
                  slot='start'
                  name={option.name}
                  value={`${option.id}`}
                  checked={isOptionSelected(option)}
                  disabled={isOptionDisabled(option)}
                  onClick={() => onOptionChange(option)}
                  style={{
                    '--background-checked': isOptionSelected(option) ? selectedColor : '#17385a',
                  }}
                />
              </IonItem>
            )
          })}
        {searchValue.length > 0 && !filteredOptions.length && (
          <IonItem key={'no_options_found'}>
            <IonLabel>
              {t('common.NoSearchResults')}
              {`"${searchValue}"`}
            </IonLabel>
          </IonItem>
        )}
      </IonList>
      {getPnnvSection()}
    </div>
  )
}

export default MultiPickList
